import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';

const SignupForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/signup`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.toLowerCase(),
          password: password
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Signup failed');
      }

      // Redirect to the URL returned by the backend (redirect_url instead of checkout_url)
      window.location.href = data.redirect_url;
    } catch (err) {
      setError(err.message || 'An error occurred during signup');
      if (err.message.includes("400")) {
        setError('Email already registered');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box
      sx={{
        width: '100%',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
        borderRadius: '24px',
        border: '1px solid rgba(25, 118, 210, 0.1)',
        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
        p: 4,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 20px 48px -12px rgba(0,0,0,0.15)',
          transform: 'translateY(-4px)'
        },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #1976d2, #42a5f5)'
        }
      }}
    >
      <Typography 
        variant="h5" 
        sx={{
          fontWeight: 700,
          mb: 3,
          color: '#0A2540',
          textAlign: 'center',
          letterSpacing: '-0.02em'
        }}
      >
        Sign Up
      </Typography>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: '12px',
            '& .MuiAlert-icon': {
              alignItems: 'center'
            }
          }}
        >
          {error}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          width: '100%',
        }}
      >
        <TextField
          fullWidth
          label="Email"
          type="email"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon sx={{ color: '#1976d2' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
              '&:hover': {
                boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)'
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 15px -3px rgba(25, 118, 210, 0.2)'
              }
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.95rem'
            }
          }}
        />

        <TextField
          fullWidth
          label="Password"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: '#1976d2' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleTogglePasswordVisibility}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
              '&:hover': {
                boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)'
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 15px -3px rgba(25, 118, 210, 0.2)'
              }
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.95rem'
            }
          }}
        />

        <TextField
          fullWidth
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          variant="outlined"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: '#1976d2' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleToggleConfirmPasswordVisibility}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
              '&:hover': {
                boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)'
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 15px -3px rgba(25, 118, 210, 0.2)'
              }
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.95rem'
            }
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!email || !password || !confirmPassword || loading}
          sx={{
            mt: 2,
            py: 2,
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: '1.1rem',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
            boxShadow: '0 8px 20px -6px rgba(25, 118, 210, 0.5)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
              boxShadow: '0 12px 24px -8px rgba(25, 118, 210, 0.6)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </Box>

      <Typography variant="body1" sx={{
        mt: 3,
        textAlign: 'center',
        color: '#546E7A'
      }}>
        Already have an account?{' '}
        <Button
          variant="text"
          sx={{
            p: 0,
            ml: 1,
            textTransform: 'none',
            fontWeight: 600,
            color: '#1976d2',
            '&:hover': { 
              backgroundColor: 'transparent',
              textDecoration: 'underline'
            }
          }}
          onClick={() => window.location.href = '/login'}
        >
          Log in
        </Button>
      </Typography>
    </Box>
  );
};

export default SignupForm;
